import BbsButton from '@/components/BbsButton';
import LocalTellPrifixSelectBox from '@/components/LocalTellPrifixSelectBox';
import PhonePrifixSelectBox from '@/components/PhonePrifixSelectBox';
import TeamSelectBox from '@/components/TeamSelectBox';
import handleError from '@/lib/handle-error';
import { useApi } from '@/provider';
import { pstring } from '@cp949/pjs';
import type { CLCode, CLUser, CLUserStateKey } from '@local/domain';
import { CLUserState } from '@local/domain';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatEpochSeconds, isEnterOrTabKeyEvent, requestFocusSelector } from '@local/ui';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Grid2,
  Select,
  MenuItem,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { UserUpdateSchemaType } from './user-update-schema';
import { userUpdateSchema } from './user-update-schema';
import errorCustomHandle from '@/lib/error-custom-handle';
import CLCodeListLabel from '@/components/CLCodeListLabel';

const rootSx: SxProps<Theme> = (theme) => ({
  '& .UserMgmtInfoForm-basicInfo': {
    '& > .MuiGrid2-root': {
      '& .MuiTypography-subtitle1': {
        minWidth: 170,
        fontSize: '0.9rem',
        color: theme.palette.primary.main,
      },
      '& .MuiTypography-h5': {
        flex: 1,
      },
    },
  },
});

type Props = {
  userData: CLUser;
  onClose: () => void;
};

export default function UserMgmtUpdateForm(props: Props) {
  const { userData, onClose } = props;
  const {
    lgonId,
    userNm,
    userState,
    lastLgonDttm,
    teamId,
    userGd,
    teamNm,
    lastPswdChngDttm,
    userId,
    pswdInitYn,
    chngDttm,
    regDttm,
    resignYn,
  } = userData;
  const api = useApi();
  const rootRef = useRef();
  const [loading, setLoading] = useState(false);
  const [changeDateStr, changeDate] = formatEpochSeconds(lastLgonDttm ?? 0);
  const [checkPswdInitYn, setCheckPswdInitYn] = useState<string>(pswdInitYn ?? 'N');
  const [selectedTeamId, setSelectedTeamId] = useState<number>();
  const [lastPswdChngDttmStr, lastPwdChng] = formatEpochSeconds(lastPswdChngDttm ?? 0);
  const [resignYnSel, setResignYnSel] = useState<string>(resignYn ?? 'N');
  const [userGdSel, setUserGdSel] = useState<string>(userGd);
  const [userGdCodeData, setUUserGdCodeData] = useState<CLCode[]>();
  // 등록일시
  const [regDttmStr, regDttmDate] = formatEpochSeconds(regDttm);
  // 변경일시
  const [chngDttmStr, chngDttmDate] = formatEpochSeconds(chngDttm);
  const formConfig = useForm<UserUpdateSchemaType>({
    resolver: yupResolver(userUpdateSchema),
    mode: 'all',
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: {
      userName: userNm,
    },
  });

  useEffect(() => {
    setSelectedTeamId(teamId);
  }, [teamId]);

  //포커스 이동 함수
  const focus = useCallback((selector: string) => {
    requestFocusSelector(rootRef.current, selector, 0);
  }, []);

  const doSave = useCallback(
    async (params: {
      userId: number;
      userName: string;
      userState: CLUserStateKey;
      pswdInitYn?: string;
      userGd?: string;
      resignYn?: string;
      teamId?: string;
    }): Promise<string | null> => {
      try {
        // 수정
        const { body } = await api.userManage.update(params);
        const { lgonId } = body;
        return lgonId;
      } catch (err) {
        // handleError(err)
        errorCustomHandle(err);
      }

      return null;
    },
    [api],
  );

  //저장버튼 클릭했을때
  const onSubmitHandle = (data: UserUpdateSchemaType) => {
    const updateData = {
      userId: userId,
      userName: data.userName,
      userState: userState,
      pswdInitYn: checkPswdInitYn,
      userGd: userGdSel,
      resignYn: resignYnSel,
      teamId: String(selectedTeamId) == 'null' ? undefined : String(selectedTeamId),
    };

    doSave(updateData).then((result) => {
      if (!result) {
        onClose();
      }
    });
  };

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = formConfig;

  const canEdit = !isSubmitting && !loading;
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userGdCodeApi = await api.clCode.search({
          rowsPerPage: 9999,
          pageNumber: 0,
          codeType: '140',
        });
        setUUserGdCodeData(userGdCodeApi.body.pagerData.elements);
      } catch (error) {
        console.error('Error fetching team data:', error);
      }
    };

    fetchData(); // 컴포넌트가 마운트될 때 한 번만 데이터를 가져오도록 함
  }, [api]); // 빈 배열을 넣어 한 번만 호출하도록 함
  return (
    <>
      <DialogContent dividers className="UserMgmtInfoDialog-content">
        <Box sx={rootSx} ref={rootRef} className="UserMgmtUpdateForm-root">
          <Stack direction="row" justifyContent="space-between" px={4}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h5">로그인 아이디</Typography>
              <Typography variant="subtitle1" color="secondary">
                {lgonId}
              </Typography>
            </Stack>
            <Paper sx={{ px: 1.5, py: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <Typography color="primary" sx={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  사용자 상태
                </Typography>
                <Chip
                  label={CLUserState[userState]}
                  variant="outlined"
                  color={userState === 'ACTIVE' ? 'success' : 'warning'}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                  }}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <Typography color="primary" sx={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  로그인 실패횟수
                </Typography>
                <Typography variant="body1">{}0</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <Typography color="primary" sx={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  최종 로그인일시
                </Typography>
                <Typography variant="body1">{changeDateStr ?? '-'}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <Typography color="primary" sx={{ fontSize: '0.85rem', fontWeight: 400 }}>
                  최종 비밀번호 변경일
                </Typography>
                <Typography variant="body1">{lastPswdChngDttmStr ?? '-'}</Typography>
              </Stack>
            </Paper>
          </Stack>
          <Typography variant="caption">*기본정보</Typography>
          <Paper sx={{ px: 3, py: 2, mb: 0.5 }}>
            <Grid2
              container
              rowSpacing={1.2}
              columnSpacing={6}
              className="UserMgmtInfoForm-basicInfo"
            >
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <TextField
                    {...register('userName')}
                    label="사용자 이름"
                    size="small"
                    fullWidth
                    placeholder="이름을 입력하세요"
                    error={!!errors.userName}
                    helperText={errors.userName?.message}
                    onKeyDown={(e) => {
                      if (isEnterOrTabKeyEvent(e)) {
                        const value = (e.target as HTMLInputElement).value;
                        if (pstring.isNotBlank(value)) {
                          e.preventDefault();
                          focus('input[name=bryyMndy]');
                        }
                      }
                    }}
                  />
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">팀 선택</Typography>
                  <TeamSelectBox
                    onSelected={(data) => {
                      if (data === undefined) return;
                      setSelectedTeamId(data?.teamId);
                    }}
                    initialValue={{ teamId: teamId, teamNm: teamNm }}
                  />
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">사용자 등급</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={userGdSel}
                    onChange={(e) => setUserGdSel(e.target.value)}
                  >
                    {userGdCodeData?.map((el, idx) => {
                      return <MenuItem value={el.code}>{el.codeNm}</MenuItem>;
                    })}
                  </Select>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">퇴사 여부</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={resignYnSel}
                    onChange={(e) => setResignYnSel(e.target.value)}
                  >
                    <MenuItem value={'Y'}>Y</MenuItem>
                    <MenuItem value={'N'}>N</MenuItem>
                  </Select>
                </Stack>
              </Grid2>
            </Grid2>
          </Paper>
          <Typography variant="caption">*상세정보</Typography>
          <Paper sx={{ px: 3, py: 2 }}>
            <Grid2
              container
              rowSpacing={1.5}
              columnSpacing={6}
              className="UserMgmtInfoForm-basicInfo"
            >
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">등록일</Typography>
                  <Typography variant="body1"> {regDttmStr}</Typography>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">수정일</Typography>
                  <Typography variant="body1"> {chngDttmStr}</Typography>
                </Stack>
              </Grid2>
            </Grid2>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" flex={1} justifyContent="flex-end">
          <Box>
            <BbsButton
              size="small"
              text="저장"
              onClick={handleSubmit(onSubmitHandle)}
              color="secondary"
            />
            <Button size="small" onClick={(_) => onClose()}>
              닫기
            </Button>
          </Box>
        </Stack>
      </DialogActions>
    </>
  );
}
